import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * The app is themed by SIX role colours the user controls freely from Settings:
 *   bg        — app background (--bone)
 *   card      — card / panel surface (--bone-warm)
 *   text      — primary text (--graphite)
 *   muted     — hairline / muted borders (--hairline)
 *   primary   — brand accent (--accent)
 *   secondary — partner accent (--partner)
 *
 * Presets populate all six at once as a starting point.
 */
export const PRESETS = {
  chalk: {
    label: 'Chalk & Iron', mode: 'light',
    colors: { bg: '#EDEAE3', card: '#F4F1EA', text: '#191C21', muted: '#DCD7CB', primary: '#C0392F', secondary: '#1E5AA0', frame: '#2A2724' },
  },
  sand: {
    label: 'Sand & Sky', mode: 'light',
    colors: { bg: '#F5EBD7', card: '#FBF5E7', text: '#233248', muted: '#E4D6BA', primary: '#E36414', secondary: '#2F73B6', frame: '#3A2E1E' },
  },
  candy: {
    label: 'Neon Pop', mode: 'light',
    colors: { bg: '#F7F6FF', card: '#FFFFFF', text: '#161031', muted: '#D8D4EE', primary: '#E5006A', secondary: '#00A6D6', frame: '#1A1440' },
  },
  blackout: {
    label: 'Blackout', mode: 'dark',
    colors: { bg: '#0B0D11', card: '#141821', text: '#EDEAE3', muted: '#1F242F', primary: '#EF5A50', secondary: '#4C8FD1', frame: '#000000' },
  },
  noir: {
    label: 'Deep Ocean', mode: 'dark',
    colors: { bg: '#0C1B2A', card: '#132635', text: '#DCE5F0', muted: '#1D3244', primary: '#22C1A5', secondary: '#3A80C4', frame: '#050C14' },
  },
  linen: {
    label: 'Linen', mode: 'light',
    colors: { bg: '#F9F5EE', card: '#FFFFFF', text: '#2A2724', muted: '#E7E1D6', primary: '#8A5A44', secondary: '#5B7A6E', frame: '#3B3428' },
  },
};

export const PRESET_COLORS = PRESETS.chalk.colors;

const STORAGE_KEY = 'breth_custom_colors';
const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

const VAR_MAP = {
  bg: '--bone',
  card: '--bone-warm',
  text: '--graphite',
  muted: '--hairline',
  primary: '--accent',
  secondary: '--partner',
  frame: '--frame',
};

function applyColorsToDom(colors) {
  const root = document.documentElement;
  Object.entries(colors).forEach(([role, value]) => {
    const varName = VAR_MAP[role];
    if (varName && value) root.style.setProperty(varName, value);
  });
  // Rough mode heuristic — pick text vs bg luminance
  const bgHex = (colors.bg || '#EDEAE3').replace('#', '');
  const r = parseInt(bgHex.substr(0, 2), 16);
  const g = parseInt(bgHex.substr(2, 2), 16);
  const b = parseInt(bgHex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  root.setAttribute('data-mode', luminance < 0.5 ? 'dark' : 'light');
}

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const [colors, setColorsState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...PRESET_COLORS, ...JSON.parse(raw) };
    } catch {}
    return { ...PRESET_COLORS };
  });

  useEffect(() => { applyColorsToDom(colors); }, [colors]);

  // Sync from server on user load (server is source of truth if present)
  useEffect(() => {
    if (!user) return;
    if (user.custom_colors && Object.keys(user.custom_colors).length) {
      const merged = { ...PRESET_COLORS, ...user.custom_colors };
      setColorsState(merged);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch {}
      return;
    }
    // Legacy path: user has an old theme name — hydrate its colours
    if (user.theme && PRESETS[user.theme]) {
      const merged = { ...PRESETS[user.theme].colors };
      setColorsState(merged);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch {}
    }
  }, [user]);

  const setColors = useCallback(async (next, { persist = true } = {}) => {
    const merged = { ...PRESET_COLORS, ...next };
    setColorsState((prev) => {
      // Shallow-equal short-circuit prevents render loops when callers pass a
      // new object each tick containing identical values.
      let same = true;
      for (const k of Object.keys(merged)) {
        if ((prev[k] || '').toLowerCase() !== (merged[k] || '').toLowerCase()) { same = false; break; }
      }
      return same ? prev : merged;
    });
    if (persist) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch {}
      try {
        await axios.put(`${API_URL}/auth/me`, { custom_colors: merged }, { withCredentials: true });
      } catch (e) { console.warn('[breth] custom_colors sync failed', e); }
    }
  }, []);

  const applyPreset = useCallback((presetKey) => {
    const preset = PRESETS[presetKey];
    if (!preset) return;
    setColors(preset.colors, { persist: true });
  }, [setColors]);

  const resetColors = useCallback(() => {
    setColors(PRESET_COLORS, { persist: true });
  }, [setColors]);

  return (
    <ThemeContext.Provider value={{ colors, setColors, presets: PRESETS, applyPreset, resetColors }}>
      {children}
    </ThemeContext.Provider>
  );
};
